/**
 * AI structuring for workstreams — the AI-STRUCTURE step of the team-knowledge
 * pipeline:
 *
 *   AUTO-COLLECT → AI-STRUCTURE → HUMAN-VERIFY → TEAM-CONSUME
 *
 * TODAY (this batch): a human pastes free-form notes (the manual stand-in for
 * AUTO-COLLECT) and this helper uses the REAL Bedrock converse op
 * (`api.inference.converse`) to STRUCTURE those notes into a partial
 * {@link WorkstreamDraft}. The result is returned to the form so a human can
 * VERIFY/edit before saving; nothing is persisted here and no field is trusted
 * blindly.
 *
 * EXTENSION POINT (AUTO-COLLECT — not available yet): when Jira / repo / deploy
 * integrations land, an upstream collector will produce the same free-form
 * "notes" (or richer structured signals) automatically and feed them into this
 * exact function — the AI-STRUCTURE → HUMAN-VERIFY → TEAM-CONSUME tail is
 * unchanged. Wire the collector to call {@link structureWorkstreamFromNotes}
 * (or a sibling that accepts structured signals) and surface the draft in the
 * same review UI. This mirrors how SpecStudio (code-Knowledge) is deferred:
 * the consuming shape is stable, only the source is swapped.
 *
 * Strict layering: this is a pure feature-level helper that receives the
 * {@link InferenceApi} (via the facade) from the hook; it never touches fetch.
 */

import type { InferenceApi } from '../../api';
import type { WorkstreamDraft } from './types';
import { DEFAULT_STAGE, WORKSTREAM_STAGES } from './workstreamConfig';

/**
 * Model used for structuring. Kept local so the workstreams feature is not
 * coupled to the chat feature.
 *
 * TODO: Confirm the exact inference profile / model id the backend is
 * provisioned for; this Claude Sonnet id is a sensible default and is swapped
 * once Bedrock model access is finalized for the EngineeringVector account.
 */
export const STRUCTURE_MODEL_ID = 'global.anthropic.claude-haiku-4-5-20251001-v1:0';

const STRUCTURE_MAX_TOKENS = 1024;
const STRUCTURE_TEMPERATURE = 0.1;

/** The subset of draft fields the structurer attempts to populate. */
export type StructuredWorkstreamFields = Partial<
  Pick<
    WorkstreamDraft,
    | 'title'
    | 'why'
    | 'whatChanging'
    | 'systemsAffected'
    | 'technicalApproach'
    | 'expectedImpact'
    | 'currentStage'
    | 'dependencies'
  >
>;

function buildSystemPrompt(): string {
  const stages = WORKSTREAM_STAGES.map((s) => s.value).join(', ');
  return [
    'You structure an engineer\'s free-form notes about what they are working',
    'on into a concise, factual JSON object describing ONE workstream.',
    '',
    'Return ONLY a single JSON object (no prose, no markdown fences) with these',
    'optional string / string[] keys:',
    '- title: short title of the work',
    '- why: why the work is being done (motivation / impact)',
    '- whatChanging: what is actually changing (systems, behavior, contracts)',
    '- systemsAffected: string[] of affected systems/services/packages',
    '- technicalApproach: the technical approach being taken',
    '- expectedImpact: string[] of expected outcomes / impact bullets',
    `- currentStage: one of [${stages}] (best guess; omit if unclear)`,
    '- dependencies: string[] of dependencies or blockers',
    '',
    'Rules: Only use information present in the notes. Do NOT invent facts,',
    'names, metrics, or systems. Omit any key you cannot fill from the notes.',
    'Keep values concise. Never include productivity, velocity, commit-count,',
    'or ranking judgements — describe the work, not the person.',
  ].join('\n');
}

/** Extracts the first balanced JSON object from a model response string. */
function extractJsonObject(text: string): unknown {
  const trimmed = text.trim();
  // Strip a leading/trailing markdown code fence if present.
  const fenced = trimmed.replace(/^```(?:json)?\s*/i, '').replace(/\s*```$/i, '');
  const start = fenced.indexOf('{');
  const end = fenced.lastIndexOf('}');
  if (start === -1 || end === -1 || end <= start) {
    throw new Error('The model did not return a JSON object.');
  }
  return JSON.parse(fenced.slice(start, end + 1));
}

function asString(value: unknown): string | undefined {
  return typeof value === 'string' && value.trim() ? value.trim() : undefined;
}

function asStringList(value: unknown): string[] | undefined {
  if (!Array.isArray(value)) return undefined;
  const out = value
    .filter((v): v is string => typeof v === 'string')
    .map((v) => v.trim())
    .filter(Boolean);
  return out.length ? out : undefined;
}

/** Coerces a raw stage guess to a known stage value, else the default. */
function normalizeStage(value: unknown): string | undefined {
  const s = asString(value);
  if (!s) return undefined;
  const match = WORKSTREAM_STAGES.find(
    (opt) => opt.value.toLowerCase() === s.toLowerCase(),
  );
  return match ? match.value : DEFAULT_STAGE;
}

/**
 * Structures free-form notes into partial workstream fields via the real
 * Bedrock converse op. Throws a friendly error if inference fails or the output
 * is not parseable — the caller keeps the human's existing draft intact.
 */
export async function structureWorkstreamFromNotes(
  inference: InferenceApi,
  notes: string,
): Promise<StructuredWorkstreamFields> {
  const clean = notes.trim();
  if (!clean) return {};

  const response = await inference.converse({
    modelId: STRUCTURE_MODEL_ID,
    systemPrompt: buildSystemPrompt(),
    prompt: `Notes:\n${clean}\n\nJSON:`,
    maxTokens: STRUCTURE_MAX_TOKENS,
    temperature: STRUCTURE_TEMPERATURE,
  });

  const parsed = extractJsonObject(response.outputText) as Record<string, unknown>;

  const fields: StructuredWorkstreamFields = {};
  const title = asString(parsed.title);
  if (title) fields.title = title;
  const why = asString(parsed.why);
  if (why) fields.why = why;
  const whatChanging = asString(parsed.whatChanging);
  if (whatChanging) fields.whatChanging = whatChanging;
  const technicalApproach = asString(parsed.technicalApproach);
  if (technicalApproach) fields.technicalApproach = technicalApproach;
  const systemsAffected = asStringList(parsed.systemsAffected);
  if (systemsAffected) fields.systemsAffected = systemsAffected;
  const expectedImpact = asStringList(parsed.expectedImpact);
  if (expectedImpact) fields.expectedImpact = expectedImpact;
  const dependencies = asStringList(parsed.dependencies);
  if (dependencies) fields.dependencies = dependencies;
  const currentStage = normalizeStage(parsed.currentStage);
  if (currentStage) fields.currentStage = currentStage;

  return fields;
}
